// src/pages/CreateCard.tsx
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useCardCreation } from "../hooks/useCardCreation";
import { StudioProvider } from "../contexts/CardCreationContext";
import { ArchetypeId } from "../constants/archetypes";
import { getTemplateByArchetype } from "../constants/templates";
import { initializeCustomizationFromArchetype } from "../utils/cardUtils";
import StudioLayout from "../components/studio/StudioLayout";
import { StepId } from "../types/studio.types";
import { DebugStyles } from "../components/DebugStyles";
import { tiktokPixelService } from "../services/tiktok-pixel.service";
import { useAppStore } from "../store/app.store";
import { useCampaign } from "../campaigns/providers/CampaignProvider";
import { PageLoader } from "../components/common/PageLoader";
import { privacyConsentService } from "../services/privacy-consent.service";
import { encryptedLocalStorageService } from "../services/encrypted-local-storage.service";
import { privacyLogger } from "../services/privacy-logger.service";

const LOCAL_CREATE_DRAFT_KEY = "lrnsy_local_create_draft";
const PENDING_FINALIZE_KEY = "lrnsy_pending_finalize_after_auth";

const ENV = {
  isProduction: (): boolean => process.env.NODE_ENV === "production",
};

export default function CreateCard() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { activeCampaign, getCampaignBySlug } = useCampaign();
  const campaignParam = searchParams.get("campaign");
  const campaign = activeCampaign || getCampaignBySlug(campaignParam);

  const clearPendingAuthRedirect = useAppStore((state) => state.clearPendingAuthRedirect);

  const [customization, setCustomization] = useState<any>(null);
  const [activeStep, setActiveStep] = useState<StepId>(1);
  const [isInitialized, setIsInitialized] = useState(false);
  const [stylesReady, setStylesReady] = useState(false);

  const autoFinalizeTriggeredRef = useRef(false);

  const archetypeParam = searchParams.get("archetype") as ArchetypeId | null;
  const recipientNameParam = searchParams.get("recipientName");
  const intentParam = searchParams.get("intent");
  const replyToCardIdParam = searchParams.get("replyToCardId");

  const archetype = useMemo(
    () => archetypeParam || campaign?.archetype || "moments",
    [archetypeParam, campaign?.archetype]
  );

  const isReplyIntent = intentParam === "reply";

  const {
    handleSave,
    handlePayment,
    createdCard,
    savedCustomization,
    cardLoading,
    loadDraftFromStorage,
  } = useCardCreation();

  useEffect(() => {
    if (ENV.isProduction()) {
      const currentDisplay = document.body.style.display;
      document.body.style.display = "none";
      void document.body.offsetHeight;
      document.body.style.display = currentDisplay;

      const root = document.getElementById("root");
      if (root) {
        root.style.opacity = "1";
        root.style.visibility = "visible";
        void root.offsetHeight;
      }

      setStylesReady(true);
    } else {
      setStylesReady(true);
    }
  }, []);

  const readLocalCreateDraft = useCallback(async () => {
    if (!privacyConsentService.canUseFunctionalStorage()) return null;

    try {
      const parsed =
        await encryptedLocalStorageService.getEncryptedJson<any>(LOCAL_CREATE_DRAFT_KEY);
      if (!parsed?.customization) return null;
      if (parsed?.archetype !== archetype) return null;
      return parsed;
    } catch (error) {
      privacyLogger.warn("Failed to read encrypted local create draft", error);
      return null;
    }
  }, [archetype]);

  const readPendingFinalize = useCallback(() => {
    try {
      const raw = localStorage.getItem(PENDING_FINALIZE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (error) {
      console.error("Failed to read pending finalize:", error);
      return null;
    }
  }, []);

  const clearPendingFinalize = useCallback(() => {
    try {
      localStorage.removeItem(PENDING_FINALIZE_KEY);
    } catch (error) {
      console.error("Failed to clear pending finalize:", error);
    }
  }, []);

  const currentReturnPath = `${location.pathname}${location.search}`;

  const initializeCustomization = useCallback(async () => {
    if (isInitialized || customization) return;

    const template = getTemplateByArchetype(archetype);
    const initial = initializeCustomizationFromArchetype(template, user, archetype);

    if (isReplyIntent) {
      try {
        localStorage.removeItem(LOCAL_CREATE_DRAFT_KEY);
      } catch (error) {
        console.error("Failed to clear old create draft for reply intent:", error);
      }

      setCustomization({
        ...initial,
        archetype,
        recipientName: recipientNameParam || "",
        senderName: user?.name || "",
        loadingText: "Preparing your reply...",
        source: "lrnsy_reply",
        replyToCardId: replyToCardIdParam || null,
        message:
          initial.message ||
          "I received what you sent, and I wanted to respond with something just as intentional.",
      });

      setActiveStep(1);
      setIsInitialized(true);
      return;
    }

    const localDraft = await readLocalCreateDraft();

    if (localDraft) {
      setCustomization({
        ...localDraft.customization,
        loadingText: localDraft.customization.loadingText || "Preparing your surprise...",
      });
      setActiveStep((localDraft.activeStep || 1) as StepId);
      setIsInitialized(true);
      return;
    }

    if (savedCustomization) {
      setCustomization({
        ...savedCustomization,
        archetype: savedCustomization.archetype || archetype,
        loadingText: savedCustomization.loadingText || "Preparing your surprise...",
      });
      setActiveStep(3);
      setIsInitialized(true);
      return;
    }

    const sessionDraft = loadDraftFromStorage();
    if (sessionDraft?.customization?.archetype === archetype) {
      setCustomization({
        ...sessionDraft.customization,
        loadingText: sessionDraft.customization.loadingText || "Preparing your surprise...",
      });
      setIsInitialized(true);
      return;
    }

    setCustomization({
      ...initial,
      archetype,
      loadingText: "Preparing your surprise...",
    });
    setIsInitialized(true);
  }, [
    isInitialized,
    customization,
    savedCustomization,
    archetype,
    loadDraftFromStorage,
    user,
    readLocalCreateDraft,
    isReplyIntent,
    recipientNameParam,
    replyToCardIdParam,
  ]);

  useEffect(() => {
    initializeCustomization();
  }, [initializeCustomization]);

  // Continuous local autosave for all three steps
  useEffect(() => {
    if (!isInitialized || !customization) return;
    if (!privacyConsentService.canUseFunctionalStorage()) return;

    try {
      void encryptedLocalStorageService.setEncryptedJson(LOCAL_CREATE_DRAFT_KEY, {
        customization,
        activeStep,
        archetype,
        createdCardId: createdCard?.id || null,
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.error("Failed to autosave create draft:", error);
    }
  }, [customization, activeStep, archetype, createdCard?.id, isInitialized]);

  // Auto-finalize exactly once after successful auth-return
  useEffect(() => {
    if (!isInitialized || !customization || authLoading) return;
    if (!isAuthenticated) return;
    if (autoFinalizeTriggeredRef.current) return;

    const pendingFinalize = readPendingFinalize();
    if (!pendingFinalize) return;

    const samePath = pendingFinalize.returnTo === currentReturnPath;
    const sameArchetype = pendingFinalize.archetype === archetype;

    if (!samePath || !sameArchetype) return;

    autoFinalizeTriggeredRef.current = true;

    const run = async () => {
      const saved = await handleSave(customization);

      if (saved) {
        clearPendingFinalize();
        clearPendingAuthRedirect();
      } else {
        autoFinalizeTriggeredRef.current = false;
      }
    };

    void run();
  }, [
    isInitialized,
    customization,
    authLoading,
    isAuthenticated,
    currentReturnPath,
    archetype,
    handleSave,
    readPendingFinalize,
    clearPendingFinalize,
    clearPendingAuthRedirect,
  ]);

  // Once a card is successfully created after auth, keep the local draft in sync
  useEffect(() => {
    if (!createdCard?.id) return;

    clearPendingFinalize();
    clearPendingAuthRedirect();
  }, [createdCard?.id, clearPendingFinalize, clearPendingAuthRedirect]);

  useEffect(() => {
    if (!archetype) return;

    tiktokPixelService.trackViewContent(
      {
        content_id: `create_${archetype}`,
        content_type: "product",
        content_name: `Create ${archetype} Experience`,
      },
      0,
      "KES",
      `viewcontent:create:${archetype}`
    );
  }, [archetype]);

  if (!isInitialized || cardLoading || !stylesReady) {
    return <PageLoader />;
  }

  if (!customization) return <PageLoader />;
  // if (!customization) return <LoadingSpinner />;

  return (
    <>
      <DebugStyles />
      <StudioProvider
        template={null}
        user={user}
        customization={customization}
        setCustomization={setCustomization}
      >
        <StudioLayout
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          customization={customization}
          setCustomization={setCustomization}
          archetype={archetype}
          onSave={() => handleSave(customization)}
          onPayment={handlePayment}
          createdCard={createdCard}
          isEditing={!!createdCard?.id}
        />
      </StudioProvider>
    </>
  );
}
